/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';

import '@/cart/components/MiniCart/MiniCart.css';

export interface MiniCartProps extends HTMLAttributes<HTMLDivElement> {
  products?: VNode;
  productListFooter?: VNode;
  subtotal?: VNode;
  subtotalExcludingTaxes?: VNode;
  preCheckoutSection?: VNode;
  ctas?: VNode;
}

export const MiniCart: FunctionComponent<MiniCartProps> = ({
  className,
  products,
  productListFooter,
  subtotal,
  subtotalExcludingTaxes,
  preCheckoutSection,
  ctas,
  ...props
}) => {
  const dictionary = useText({
    subtotal: 'Cart.MiniCart.subtotal',
    subtotalExcludingTaxes: 'Cart.MiniCart.subtotalExcludingTaxes',
  });

  return (
    <div {...props} className={classes(['cart-mini-cart', className])}>
      {/* Content */}
      {products && (
        <>
          <div
            className="cart-mini-cart__products"
            data-testid="mini-cart-products-wrapper"
          >
            {products}
          </div>

          {/* Product List Footer */}
          {productListFooter && (
            <div
              className={classes(['cart-mini-cart__productListFooter'])}
              data-testid="mini-cart-product-list-footer"
            >
              <VComponent node={productListFooter} />
            </div>
          )}

          <div
            className="cart-mini-cart__footer"
            data-testid="mini-cart-subtotals"
          >
            {/* Subtotal */}
            {subtotal && (
              <div
                className="cart-mini-cart__footer__estimated-total"
                data-testid="mini-cart-subtotal"
              >
                {dictionary.subtotal}
                <VComponent node={subtotal} />
              </div>
            )}
            {/* Subtotal Excluding Taxes */}
            {subtotalExcludingTaxes && (
              <div
                className="cart-mini-cart__footer__estimated-total-excluding-taxes"
                data-testid="mini-cart-subtotal-excluding-taxes"
              >
                {dictionary.subtotalExcludingTaxes}
                <VComponent
                  node={subtotalExcludingTaxes}
                  className={classes([
                    'dropin-price-summary__price',
                    'dropin-price-summary__price--muted',
                  ])}
                />
              </div>
            )}

            {/* Pre-checkout section */}
            {preCheckoutSection && (
              <div
                className={classes(['cart-mini-cart__preCheckoutSection'])}
                data-testid="mini-cart-pre-checkout-section"
              >
                <VComponent node={preCheckoutSection} />
              </div>
            )}

            {ctas && (
              <VComponent
                node={ctas}
                className="cart-mini-cart__footer__ctas"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
